import React, { useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResumenTab({ userData, refreshData }) {
  const { getToken } = useAdminAuth();
  const { user, enrollment, summary, scholarship } = userData;
  
  const [showScholarshipModal, setShowScholarshipModal] = useState(false);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [showEditInfoModal, setShowEditInfoModal] = useState(false);
  const [showChangePhotoModal, setShowChangePhotoModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleChangeStatus = async (newStatus) => {
    if (!window.confirm(`¿Cambiar status a ${newStatus}?`)) return;
    
    setIsProcessing(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/users/${user.id}/enrollment`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Status actualizado correctamente');
      refreshData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar status');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImpersonate = async () => {
    if (!window.confirm('¿Impersonar este usuario? (modo solo lectura)')) return;
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/impersonate`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert(`Token de impersonación: ${response.data.token}`);
      // TODO: Implement actual impersonation logic
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al generar token');
    }
  };

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="bg-[#1a4d5c]/10 rounded-lg p-4 border border-[#1a4d5c]/30">
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Acciones Rápidas</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {summary.enrollment_status === 'preview' && (
            <button
              onClick={() => handleChangeStatus('active')}
              disabled={isProcessing}
              className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-sm font-medium disabled:opacity-50"
            >
              ✅ Activar
            </button>
          )}
          
          {summary.enrollment_status === 'active' && (
            <button
              onClick={() => handleChangeStatus('suspended')}
              disabled={isProcessing}
              className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm font-medium disabled:opacity-50"
            >
              🚫 Suspender
            </button>
          )}
          
          <button
            onClick={() => setShowScholarshipModal(true)}
            className="px-4 py-2 bg-[#D4AF37]/20 text-[#D4AF37] rounded-lg hover:bg-[#D4AF37]/30 transition-colors text-sm font-medium"
          >
            🎓 Aplicar Beca
          </button>
          
          <button
            onClick={() => setShowPaymentLinkModal(true)}
            className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-medium"
          >
            💳 Generar Link Pago
          </button>
          
          <button
            onClick={handleImpersonate}
            className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors text-sm font-medium"
          >
            👁️ Impersonar
          </button>
        </div>
      </div>

      {/* User Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Info */}
        <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Información Personal</h3>
            <button
              onClick={() => setShowEditInfoModal(true)}
              className="p-2 hover:bg-[#1a4d5c]/30 rounded-lg transition-colors"
              title="Editar información"
            >
              <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">Nombre:</span>
              <span className="text-white font-medium">{user.full_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Email:</span>
              <span className="text-white font-medium">{user.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">ID:</span>
              <span className="text-white font-mono text-sm">{user.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">País:</span>
              <span className="text-white">{user.country || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Zona Horaria:</span>
              <span className="text-white">{user.timezone || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Último acceso:</span>
              <span className="text-white text-sm">
                {user.last_login ? new Date(user.last_login).toLocaleString('es-ES') : 'Nunca'}
              </span>
            </div>
          </div>
        </div>

        {/* Enrollment Info */}
        <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
          <h3 className="text-lg font-semibold text-white mb-4">Matrícula</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Status:</span>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium border ${
                  summary.enrollment_status === 'active'
                    ? 'bg-green-500/10 text-green-400 border-green-500/30'
                    : summary.enrollment_status === 'suspended'
                    ? 'bg-red-500/10 text-red-400 border-red-500/30'
                    : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
                }`}
              >
                {summary.enrollment_status}
              </span>
            </div>
            
            {enrollment && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-400">Cohorte:</span>
                  <span className="text-white">{enrollment.cohort_id || 'Sin asignar'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Plan:</span>
                  <span className="text-white capitalize">{enrollment.plan || 'full'}</span>
                </div>
              </>
            )}

            {summary.has_scholarship && (
              <div className="mt-4 p-3 bg-[#D4AF37]/10 rounded-lg border border-[#D4AF37]/30">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400 text-sm">Beca Aplicada:</span>
                  <span className="text-[#D4AF37] font-bold text-lg">{summary.scholarship_percentage}%</span>
                </div>
                {scholarship && (
                  <>
                    <div className="text-xs text-gray-500">
                      Código: <span className="text-[#D4AF37] font-mono">{scholarship.code}</span>
                    </div>
                    {scholarship.valid_until && (
                      <div className="text-xs text-gray-500">
                        Válido hasta: {new Date(scholarship.valid_until).toLocaleDateString('es-ES')}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-[#1a4d5c]/30">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400">Total Pagado:</span>
                <span className="text-green-400 font-bold text-xl">${summary.total_paid}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Balance Pendiente:</span>
                <span className="text-white font-bold text-xl">${summary.balance}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-gray-400 text-sm mb-1">Sesiones Asistidas</div>
          <div className="text-2xl font-bold text-white">{userData.attendance?.length || 0}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-gray-400 text-sm mb-1">Proyectos</div>
          <div className="text-2xl font-bold text-white">{userData.projects?.length || 0}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-gray-400 text-sm mb-1">Certificados</div>
          <div className="text-2xl font-bold text-white">{userData.certificates?.length || 0}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-gray-400 text-sm mb-1">Tickets Soporte</div>
          <div className="text-2xl font-bold text-white">{userData.tickets?.length || 0}</div>
        </div>
      </div>

      {/* Modals */}
      {showScholarshipModal && (
        <ScholarshipModal
          userId={user.id}
          onClose={() => setShowScholarshipModal(false)}
          onSuccess={() => {
            setShowScholarshipModal(false);
            refreshData();
          }}
        />
      )}

      {showPaymentLinkModal && (
        <PaymentLinkModal
          userId={user.id}
          balance={summary.balance}
          onClose={() => setShowPaymentLinkModal(false)}
          onSuccess={() => {
            setShowPaymentLinkModal(false);
            refreshData();
          }}
        />
      )}

      {/* Edit Info Modal */}
      {showEditInfoModal && (
        <EditInfoModal
          user={user}
          onClose={() => setShowEditInfoModal(false)}
          onSuccess={() => {
            setShowEditInfoModal(false);
            refreshData();
          }}
        />
      )}

      {/* Change Photo Modal */}
      {showChangePhotoModal && (
        <ChangePhotoModal
          userId={user.id}
          currentPhoto={user.profile_picture_url}
          onClose={() => setShowChangePhotoModal(false)}
          onSuccess={() => {
            setShowChangePhotoModal(false);
            refreshData();
          }}
        />
      )}
    </div>
  );
}

// Scholarship Modal Component
function ScholarshipModal({ userId, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/scholarship`,
        { code, reason },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Beca aplicada correctamente');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al aplicar beca');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-md w-full p-6 border border-[#1a4d5c]/30">
        <h3 className="text-xl font-bold text-white mb-4">Aplicar Beca</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Código de Beca</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37]"
              placeholder="BECA2025"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Motivo (opcional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37]"
              rows="3"
              placeholder="Razón para aplicar esta beca..."
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg hover:from-[#F4E4B7] hover:to-[#D4AF37] disabled:opacity-50"
            >
              {isLoading ? 'Aplicando...' : 'Aplicar Beca'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Payment Link Modal Component
function PaymentLinkModal({ userId, balance, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [type, setType] = useState('full');
  const [installments, setInstallments] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const handleGenerate = async () => {
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/payment-link`,
        { type, installments: parseInt(installments) },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert(`Link generado: ${response.data.checkout_url}`);
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al generar link');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-md w-full p-6 border border-[#1a4d5c]/30">
        <h3 className="text-xl font-bold text-white mb-4">Generar Link de Pago</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Tipo de Pago</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37]"
            >
              <option value="full">Pago Completo (${balance})</option>
              <option value="installment">Cuotas</option>
              <option value="enrollment_fee">Inscripción ($497)</option>
            </select>
          </div>
          
          {type === 'installment' && (
            <div>
              <label className="block text-sm text-gray-300 mb-2">Número de Cuotas</label>
              <select
                value={installments}
                onChange={(e) => setInstallments(e.target.value)}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37]"
              >
                <option value="1">1 cuota</option>
                <option value="3">3 cuotas</option>
              </select>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              onClick={handleGenerate}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg disabled:opacity-50"
            >
              {isLoading ? 'Generando...' : 'Generar Link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Edit Info Modal
function EditInfoModal({ user, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [fullName, setFullName] = useState(user.full_name || '');
  const [email, setEmail] = useState(user.email || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [country, setCountry] = useState(user.country || '');
  const [timezone, setTimezone] = useState(user.timezone || 'America/New_York');
  const [locale, setLocale] = useState(user.locale || 'es');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!fullName.trim() || !email.trim()) {
      alert('Nombre y email son requeridos');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}`,
        {
          full_name: fullName,
          email,
          phone,
          country,
          timezone,
          locale
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Información actualizada correctamente');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar información');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-2xl w-full p-6 border border-[#1a4d5c]/30">
        <h3 className="text-xl font-bold text-white mb-4">Editar Información Personal</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm text-gray-300 mb-2">Nombre Completo *</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Juan Pérez"
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-2">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="juan@ejemplo.com"
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-2">Teléfono</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1234567890"
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-2">País</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="">Seleccionar...</option>
                <option value="US">Estados Unidos</option>
                <option value="MX">México</option>
                <option value="ES">España</option>
                <option value="AR">Argentina</option>
                <option value="CO">Colombia</option>
                <option value="CL">Chile</option>
                <option value="PE">Perú</option>
                <option value="VE">Venezuela</option>
                <option value="EC">Ecuador</option>
                <option value="UY">Uruguay</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-2">Zona Horaria</label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="America/New_York">EST (Nueva York)</option>
                <option value="America/Chicago">CST (Chicago)</option>
                <option value="America/Denver">MST (Denver)</option>
                <option value="America/Los_Angeles">PST (Los Angeles)</option>
                <option value="America/Mexico_City">Mexico City</option>
                <option value="America/Bogota">Bogotá</option>
                <option value="America/Lima">Lima</option>
                <option value="America/Santiago">Santiago</option>
                <option value="America/Buenos_Aires">Buenos Aires</option>
                <option value="Europe/Madrid">Madrid</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-2">Idioma</label>
              <select
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="es">Español</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg hover:from-[#F4E4B7] hover:to-[#D4AF37] disabled:opacity-50"
            >
              {isLoading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Change Photo Modal
function ChangePhotoModal({ userId, currentPhoto, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(currentPhoto);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMethod, setUploadMethod] = useState('file');
  const [imageUrl, setImageUrl] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('El archivo es muy grande. Máximo 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        alert('Solo se permiten imágenes');
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (uploadMethod === 'file' && !selectedFile) {
      alert('Por favor selecciona una imagen');
      return;
    }

    if (uploadMethod === 'url' && !imageUrl.trim()) {
      alert('Por favor ingresa una URL de imagen');
      return;
    }

    setIsUploading(true);
    try {
      if (uploadMethod === 'file') {
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        await axios.post(
          `${BACKEND_URL}/api/admin/users/${userId}/profile-picture`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        await axios.patch(
          `${BACKEND_URL}/api/admin/users/${userId}`,
          { profile_picture_url: imageUrl },
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
      }
      
      alert('Foto actualizada correctamente');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al subir la foto');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-md w-full p-6 border border-[#1a4d5c]/30">
        <h3 className="text-xl font-bold text-white mb-4">Cambiar Foto de Perfil</h3>
        
        <div className="space-y-4">
          <div className="flex justify-center mb-4">
            <div className="relative w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-[#D4AF37] to-[#F4E4B7] flex items-center justify-center">
              {preview ? (
                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <span className="text-black text-4xl font-bold">?</span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">Método</label>
            <div className="flex gap-2">
              <button
                onClick={() => setUploadMethod('file')}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  uploadMethod === 'file'
                    ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/50'
                    : 'bg-black/50 text-gray-400 border border-[#1a4d5c]/50'
                }`}
              >
                📁 Subir Archivo
              </button>
              <button
                onClick={() => setUploadMethod('url')}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  uploadMethod === 'url'
                    ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/50'
                    : 'bg-black/50 text-gray-400 border border-[#1a4d5c]/50'
                }`}
              >
                🔗 URL
              </button>
            </div>
          </div>

          {uploadMethod === 'file' && (
            <div>
              <label className="block text-sm text-gray-300 mb-2">Seleccionar Imagen</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#D4AF37]/20 file:text-[#D4AF37] hover:file:bg-[#D4AF37]/30 file:cursor-pointer"
              />
              <p className="text-xs text-gray-500 mt-1">Máximo 5MB. Formatos: JPG, PNG, GIF</p>
            </div>
          )}

          {uploadMethod === 'url' && (
            <div>
              <label className="block text-sm text-gray-300 mb-2">URL de la Imagen</label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => {
                  setImageUrl(e.target.value);
                  setPreview(e.target.value);
                }}
                placeholder="https://ejemplo.com/imagen.jpg"
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              onClick={handleUpload}
              disabled={isUploading || (uploadMethod === 'file' && !selectedFile) || (uploadMethod === 'url' && !imageUrl)}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg hover:from-[#F4E4B7] hover:to-[#D4AF37] disabled:opacity-50"
            >
              {isUploading ? 'Subiendo...' : 'Guardar Foto'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
